import React from "react";

function todaysDate(): number {
	return new Date().getDate();
}

/**
 * Force the containing component to update if the window is blurred and then
 * later focused on a different day.
 *
 * Returns a key which changes only when the day changes. This can be used as a
 * fetchKey for example.
 */
export function useDailyUpdate(): number {
	const [key, setKey] = React.useState(0);

	React.useEffect(() => {
		function triggerUpdate() {
			setKey(n => n + 1);
		}

		const updater = new DailyUpdater(triggerUpdate);

		updater.start();
		return () => updater.stop();
	}, []);

	return key;
}

/**
 * Encapsulation of daily update behaviour in a class so the same logic can be
 * used in hooks components as well as class components.
 *
 * For usage in a hooks component, see the `useDailyUpdate` hook.
 *
 * This can be used from a class component in a fairly straightforward way:
 * ```ts
 * class MyComponent extends React.Component {
 *   updater = new DailyUpdater(() => this.forceUpdate());
 *
 *   componentDidMount() {
 *     this.updater.start();
 *   }
 *
 *   componentWillUnmount() {
 *     this.updater.stop();
 *   }
 *
 *   render() { ... }
 * }
 * ```
 */
export class DailyUpdater {
	private dayOfMonth: number;
	private state: "stopped" | "started" = "stopped";

	constructor(private updateFn: () => void) {
		this.dayOfMonth = todaysDate();
	}

	start(): void {
		if (this.state === "started") return;
		this.state = "started";
		window.addEventListener("blur", this.updateDayOfMonth);
		window.addEventListener("focus", this.triggerUpdateIfDateChanged);
	}

	stop(): void {
		window.removeEventListener("blur", this.updateDayOfMonth);
		window.removeEventListener("focus", this.triggerUpdateIfDateChanged);
		this.state = "stopped";
	}

	private updateDayOfMonth = () => {
		this.dayOfMonth = todaysDate();
	};

	private triggerUpdateIfDateChanged = () => {
		if (todaysDate() !== this.dayOfMonth) {
			this.updateFn();
		}
	};
}
